import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, MainButton, MainText } from "app/components/common";
import theme from "app/Theme/Theme";
import normalize from "app/utils/normalize";
import CertificateHeader from "app/components/common/CertificateHeader";
import { useDispatch, useSelector } from "react-redux";
import {
  getCampaignEndDate,
  getCampaignName,
  getUserCampaignCertificate,
} from "@microdoing/shared/services/userCampaign/selectors";
import Pdf from "react-native-pdf";
import i18n from "@microdoing/shared/i18n";
import { Linking, StatusBar } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import FastImage from "react-native-fast-image";
import LogoLinkedIn from "app/components/Icons/logo/LogoLinkedIn";
import {
  isAndroid,
  isWebDesktop,
} from "@microdoing/shared/utils/deviceDetectors";
import WebDesktopView from "app/components/common/Web/WebDesktopView";
import moment from "moment";
import selectLanguage from "@microdoing/shared/utils/selectLanguage";
import { getUserLanguage } from "@microdoing/shared/services/user/selectors";
import Loader from "app/components/common/Loader";
import ArrowLeft from "app/components/Icons/Arrow/ArrowLeft";
import {
  breadCrumbsDailyFocusCertificationScreen,
  breadCrumbsProfileCertificationScreen,
} from "app/navigation/breadcrumb/BreadcrumbPaths";
import { useNavigation } from "@react-navigation/native";

const certificateRatio = 220 / 296;

interface IProps {}

const CertificateScreen = ({}: IProps) => {
  const [containerWidth, setContainerWidth] = useState(100);
  const [containerHeight, setContainerHeight] = useState(100);
  const [pdfWidth, setPdfWidth] = useState(100);
  const [pdfHeight, setPdfHeight] = useState(100);
  const [isDownloading, setIsDownloading] = useState(false);
  const canvasPdfRef = useRef(null);

  const certificationUrl = useSelector(getUserCampaignCertificate);
  const campaignName = useSelector(getCampaignName);
  const certificationEndDate = useSelector(getCampaignEndDate);
  const language = useSelector(getUserLanguage);

  const microdoingLinkedinId = "34688510";
  const certificationName = useMemo(
    () => selectLanguage({ text: campaignName, language }),
    [campaignName],
  );
  const certificationMonth = useMemo(
    () => moment(certificationEndDate).month() + 1,
    [certificationEndDate],
  );
  const certificationYear = useMemo(() => moment(certificationEndDate).year(), [
    certificationEndDate,
  ]);

  const dispatch = useDispatch();
  const navigation = useNavigation();

  const isFromProfile = useMemo(
    () => !(navigation.getState().routes[0].name === "DailyFocusScreen"),
    [navigation],
  );

  const onPdfLoaded = () => {
    if (canvasPdfRef.current) {
      canvasPdfRef.current.style.borderRadius = "10px";
    }
  };

  useEffect(() => {
    onPdfLoaded();
  }, [canvasPdfRef.current]);

  const findDimensions = ({ height, width }, setWidth, setHeight) => {
    setWidth(width);
    setHeight(width * certificateRatio);
  };

  const handleOnLayoutContainer = event => {
    findDimensions(
      event.nativeEvent.layout,
      setContainerWidth,
      setContainerHeight,
    );
  };

  const handleOnLayoutPdf = event => {
    findDimensions(event.nativeEvent.layout, setPdfWidth, setPdfHeight);
  };

  const downloadPDF = () => {
    Linking.openURL(certificationUrl);
  };

  const certificationHeader = () => {
    return (
      <Box width="100%">
        <CertificateHeader
          title={i18n.t("your-certification")}
          leftIcon={
            !isWebDesktop && (
              <ArrowLeft
                color={theme.colors["neutral.white"]}
                height={normalize(30)}
                width={normalize(30)}
              />
            )
          }
        />
        <MainText
          marginTop="m"
          variant="l4-medium"
          color="neutral.white"
          marginBottom="sm"
        >
          {i18n.t("certification-success-message")}
        </MainText>
      </Box>
    );
  };

  const insets = useSafeAreaInsets();

  const options = {
    cMapUrl: certificationUrl,
    cMapPacked: true,
  };

  if (isWebDesktop) {
    return (
      <WebDesktopView
        breadcrumbsPath={
          isFromProfile
            ? breadCrumbsProfileCertificationScreen
            : breadCrumbsDailyFocusCertificationScreen
        }
      >
        <Box
          flex={1}
          borderRadius={10}
          backgroundColor="neutral.white"
          onLayout={handleOnLayoutContainer}
        >
          <Box
            borderTopLeftRadius={10}
            borderTopRightRadius={10}
            position="absolute"
            height={containerHeight / 2}
          >
            <FastImage
              source={require("../../../../assets/images/BackgroundGradient.png")}
              style={{
                width: containerWidth,
                height: containerHeight / 2,
                borderTopLeftRadius: 10,
                borderTopRightRadius: 10,
              }}
            />
          </Box>
          <Box flex={1} marginHorizontal="webXM" flexDirection="row">
            <Box
              flex={1}
              height={containerHeight / 2}
              justifyContent="center"
              paddingTop="webSL"
              paddingRight="xxl"
            >
              {certificationHeader()}
            </Box>
            {!!certificationUrl && (
              <Box
                flex={1}
                onLayout={handleOnLayoutPdf}
                justifyContent="center"
                borderRadius={10}
                paddingTop="webSL"
              >
                <Box
                  shadowColor="shadow"
                  shadowOffset={{ width: 0, height: 5 }}
                  shadowOpacity={0.7}
                  shadowRadius={27}
                  borderRadius={10}
                >
                  <Pdf
                    paramsDocument={{
                      onLoadSuccess: onPdfLoaded,
                      options: options,
                      loading: (
                        <Box
                          backgroundColor="neutral.white"
                          width={pdfWidth}
                          height={266}
                          justifyContent="center"
                        >
                          <Loader size={normalize(30)} />
                        </Box>
                      ),
                      renderMode: "canvas",
                      file: certificationUrl,
                    }}
                    paramsPage={{
                      onLoadSuccess: onPdfLoaded,
                      canvasRef: canvasPdfRef,
                      width: pdfWidth,
                      renderAnnotationLayer: false,
                      renderMode: "canvas",
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
          <Box
            flexDirection="row"
            justifyContent="center"
            marginBottom="webXXL"
            marginHorizontal="webXM"
          >
            <Box flex={1}>
              <MainButton
                onPress={() => {
                  Linking.openURL(
                    `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${certificationName}&organizationId=${microdoingLinkedinId}&issueYear=${certificationYear}&issueMonth=${certificationMonth}&certUrl=${certificationUrl}`,
                  );
                }}
                leftIcon={
                  <Box marginRight="xs">
                    <LogoLinkedIn />
                  </Box>
                }
                isContentCenter
                text={i18n.t("share-on-linkedin")}
                color="neutral.white"
                textColor="neutral.900"
                textVariant="l5-medium"
              />
            </Box>
            <Box flex={1}>
              <MainButton
                onPress={downloadPDF}
                isLoading={isDownloading}
                text={i18n.t("download")}
                textVariant="l5-bold"
              />
            </Box>
          </Box>
        </Box>
      </WebDesktopView>
    );
  }

  return (
    <Box
      style={{ paddingTop: insets.top + theme.spacing.s }}
      top={0}
      flex={1}
      paddingHorizontal="l"
      backgroundColor="neutral.200"
      onLayout={handleOnLayoutContainer}
    >
      <StatusBar
        barStyle="light-content"
        backgroundColor={theme.colors["transparent"]}
        hidden={isAndroid}
      />
      <Box position="absolute" height={containerHeight / 4}>
        <FastImage
          source={require("../../../../assets/images/BackgroundGradient.png")}
          style={{ width: containerWidth, height: 300 }}
        />
      </Box>
      {certificationHeader()}
      <Box
        marginTop="s"
        marginBottom="m"
        width="100%"
        onLayout={handleOnLayoutPdf}
      >
        {!!certificationUrl && (
          <Pdf
            trustAllCerts={false}
            source={{ uri: certificationUrl }}
            style={{
              backgroundColor: "transparent",
              width: pdfWidth,
              height: pdfHeight,
              borderRadius: normalize(8),
            }}
            onError={error => {
              console.log(`error` + error);
            }}
          />
        )}
      </Box>
      <MainButton
        marginBottom="xs"
        onPress={downloadPDF}
        isLoading={isDownloading}
        text={i18n.t("download")}
        textVariant="l5-bold"
      />
      <MainButton
        onPress={() => {
          Linking.openURL(
            `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${certificationName}&organizationId=${microdoingLinkedinId}&issueYear=${certificationYear}&issueMonth=${certificationMonth}&certUrl=${certificationUrl}`,
          );
        }}
        leftIcon={
          <Box marginRight="xs">
            <LogoLinkedIn />
          </Box>
        }
        isContentCenter
        text={i18n.t("share-on-linkedin")}
        color="neutral.white"
        textColor="neutral.900"
        textVariant="l5-medium"
      />
    </Box>
  );
};

export default CertificateScreen;
